import { Exclude, Expose, Type } from 'class-transformer';
import {
  BaseModel,
  BoardResolutionMode,
  BusinessOwnership,
  CrsTaxResidenceModel,
  DATEPICKER,
  ESubmissionMethod,
  Method,
  MinimumAmountReceiveNotification,
  PeriodUnit,
  PersonnelAppointment,
  ProductProgram,
  ToStartOfLocalDateString,
} from '@core/data-access';
import { CollateralPropertyModel } from './about-your-business.model';
import { BoardResolutionApprovingPersonsModel } from './business-declaration.model';
import { PersonnelModel } from './personnel.model';
import { BeneficialOwnerModel } from './beneficial-owner.model';
import { UsPersonModel } from './us-person.model';
import { CompanyModel } from './my-info-business.model';
import { AddressModel } from '@core/address';
import { CategoryCode, CorppassProfile, PositionCode } from '@core/singpass';
import { DeepPartial, Default, Helper } from '@core/util';
import { ObjectNumber } from '../type';

@Exclude()
export class AddressMetadata extends BaseModel {
  @Expose()
  @Default(false)
  maillingAddressDifferent!: boolean;
}

@Exclude()
export class ApplicationProduct extends BaseModel {
  @Expose() id!: number;

  @Expose() businessUnitId!: number;

  @Expose() title!: string;

  @Expose() type!: string;

  @Expose() status!: string;

  @Expose() productProgram!: ProductProgram;

  @Expose() collateralRequirement!: boolean;

  @Expose() esgRequirement!: boolean;

  @Expose() islamicRequirement!: boolean;

  @Expose() accessibility!: string;
}

@Exclude()
export class ApplicationCompany {
  @Expose()
  id!: number;

  @Expose()
  businessUnitId!: number;

  @Expose()
  caseId!: number;

  @Expose()
  leadId!: number;

  @Expose()
  productId!: number;

  @Expose()
  source!: string;

  @Expose()
  sourceOther!: string;

  @Expose()
  revenue!: number;

  @Expose()
  uen!: string;

  @Expose()
  companyName!: string;

  @Expose()
  contactName!: string;

  @Expose()
  contactEmail!: string;

  @Expose()
  hashtag!: string;

  @Expose()
  remark!: string;

  @Expose()
  contactPhoneAreaCode!: string;

  @Expose()
  contactPhoneNumber!: string;

  @Expose()
  createdById!: number;

  @Expose()
  esgServiceVsGood!: string;
}

@Exclude()
export class ApplicationFinancing extends BaseModel {
  @Expose() id!: number;
  @Expose() businessUnitId!: number;
  @Expose() caseId!: number;
  @Expose() facilityId!: number;
  @Expose() facilityType!: string;
  @Expose() facilityTitle!: string;
  @Expose() currency!: string;
  @Expose() amount!: number;
  @Expose() amountSGD!: number;
  @Expose() exchangeRate!: number;
  @Expose() tenor!: number;
  @Expose() tenorUnit!: PeriodUnit;
  @Expose() appliedTenor!: number;
  @Expose() appliedTenorUnit!: PeriodUnit;
  @Expose() commitedStatus!: string;
  @Expose() limitMinimumValue!: number;
  @Expose() limitMaximumValue!: number;
  @Expose() interestServicing!: number;
  @Expose() instalment!: number;
  @Expose() instalmentSGD!: number;
  @Expose() tenorMinimumValue!: number;
  @Expose() tenorMaximumValue!: number;
  @Expose() remark!: string;
}

@Exclude()
export class ApplicationBusinessEntity extends BaseModel {
  @Expose() id!: number;

  @Expose() businessUnitId!: number;

  @Expose() caseId!: number;

  @Expose() businessClassification!: string;

  @Expose() businessClassificationOther!: string;

  @Expose() countryId!: number;

  @Expose() natureOfBusiness!: string;

  @Expose() secondaryNatureOfBusiness!: string;

  @Expose() businessConstitution!: string;

  @Expose() businessStatus!: string;

  @Expose() businessType!: string;

  @Expose()
  commencementDate!: string;

  @Expose()
  @ToStartOfLocalDateString(DATEPICKER.PATTERN)
  commencementDateOnly!: string;

  @Expose()
  businessExpiryDate!: string;

  @Expose()
  @ToStartOfLocalDateString(DATEPICKER.PATTERN)
  businessExpiryDateOnly!: string;

  @Expose() businessActivity!: string;

  @Expose() businessActivityOther!: string;

  @Expose() numberOfEmployee!: number;

  @Expose() registeredAddress!: string;

  @Expose() mailingAddress!: string;

  @Expose() isSameAsRegisteredAddress!: boolean;

  @Expose() tin!: string;

  @Expose() officeContactAreaCode!: string;

  @Expose() officeContactNumber!: string;

  @Expose() officeFaxAreaCode!: string;

  @Expose() officeFaxNumber!: string;

  @Expose() businessPremiseOwnership!: string;

  @Expose() businessOwnership!: BusinessOwnership;

  @Expose() monthlyRental!: number;

  @Expose() gstRegistered!: boolean;

  @Expose() email!: string;

  @Expose() ntbETB!: string;

  @Expose() bankingGroup!: string;

  @Expose() originatingUnit!: string;

  @Expose() centricName!: string;

  @Expose() remark!: string;

  @Expose() companyProfitable!: boolean;

  @Expose() propertyCorporation!: boolean;

  @Expose() participateIndirectly!: boolean;

  @Expose() usCorporation!: boolean;

  @Expose() usIndicia!: boolean;

  @Expose()
  @Default([])
  addresses!: AddressModel[];

  @Expose()
  @Default(() => AddressMetadata.createEmpty())
  metadata!: AddressMetadata;
}

@Exclude()
export class ApplicationBoardResolutionModel extends BaseModel {
  @Expose() mode!: BoardResolutionMode;

  @Expose()
  @Type(() => BoardResolutionApprovingPersons)
  approvingPersons!: BoardResolutionApprovingPersons[];
}

@Exclude()
export class BoardResolutionApprovingPersons extends BaseModel {
  @Expose() keyPersonnelId!: number;
}

@Exclude()
export class ApplicationForCorporateAccount extends BaseModel {
  @Expose() id!: number;

  @Expose() accountOpeningNotRequired!: boolean;

  @Expose() accountNumber!: string;

  @Expose() nonIslamicType!: boolean; // TODO: change type

  @Expose() nitDepositsAccountType!: string; // TODO: change type

  @Expose() nitCurrencyType!: string; // TODO: change type

  @Expose() islamicType!: boolean;

  @Expose() itDepositsAccountType!: string; // TODO: change type

  @Expose() itCurrencyType!: string; // TODO: change type

  @Expose() purposeOfAccount!: string; // TODO: change type

  @Expose() purposeOfAccountOther!: string;

  @Expose() primarySourceOfFunds!: string; // TODO: change type

  @Expose() primarySourceOfFundsOther!: string;

  @Expose() signingConditionsOfAuthorisedSignatory!: string; // TODO: change type

  @Expose() notAutoRegistration!: boolean;

  @Expose() minimumAmountReceiveNotification!: MinimumAmountReceiveNotification;

  @Expose() notUseCorporateEAlertsService!: boolean;

  @Expose() remark!: string;

  @Expose() metadata!: object;
}

@Exclude()
export class ApplicationSnapshot extends BaseModel {
  @Expose() product!: ApplicationProduct;
}

@Exclude()
export class ApplicationModel extends BaseModel {
  @Expose() appId!: string;

  @Expose()
  @Type(() => ApplicationForCorporateAccount)
  applicationForCorporateAccount!: ApplicationForCorporateAccount;

  @Expose()
  @Type(() => ApplicationBoardResolutionModel)
  boardResolution!: ApplicationBoardResolutionModel;

  @Expose()
  @Type(() => ApplicationSnapshot)
  snapshot!: ApplicationSnapshot;

  @Expose()
  @Type(() => CompanyModel)
  company!: CompanyModel;

  @Expose()
  @Type(() => ApplicationBusinessEntity)
  businessEntity!: ApplicationBusinessEntity;

  @Expose()
  @Default([])
  @Type(() => PersonnelModel)
  keyPersonnels!: PersonnelModel[];

  @Expose()
  @Default([])
  @Type(() => BeneficialOwnerModel)
  beneficialOwners!: BeneficialOwnerModel[];

  @Expose()
  @Default([])
  @Type(() => UsPersonModel)
  usPersons!: UsPersonModel[];

  @Expose()
  @Type(() => ApplicationFinancing)
  financings!: ApplicationFinancing[];

  @Expose()
  @Type(() => CollateralPropertyModel)
  collateralProperties!: CollateralPropertyModel[];

  @Expose() preferredContactTime!: string[];

  @Expose() creditFinancingConnectedParty!: boolean;

  @Expose() typeOfFinancial!: string;

  @Expose() isEAdvice!: boolean;

  @Expose() isContactSMS!: boolean;

  @Expose() isFax!: boolean;

  @Expose()
  @Default([])
  @Type(() => CrsTaxResidenceModel)
  crs!: CrsTaxResidenceModel[];

  get sumFacilityAmount() {
    let sum = 0;
    if (this.financings) {
      this.financings.forEach(item => {
        sum += item.amountSGD;
      });
    }
    return sum;
  }

  get sumTenorAmount(): number {
    let sum = 0;
    if (this.financings) {
      this.financings.forEach(item => {
        const multiplier = item.appliedTenorUnit === PeriodUnit.MONTHS ? 1 : 12;
        sum += item.appliedTenor * multiplier;
      });
    }
    return sum;
  }

  get personnelsMap(): Map<number, PersonnelModel> {
    const personnelsMap = new Map<number, PersonnelModel>();
    Array.from(this.keyPersonnels.entries()).forEach(([, personnel]) => personnel.id && personnelsMap.set(personnel.id, personnel));
    return personnelsMap;
  }

  bindingPersonnelsToBeneficialOwners(): BeneficialOwnerModel[] {
    const array: BeneficialOwnerModel[] = [];
    this.beneficialOwners?.forEach(bo => {
      if (!bo.keyPersonnelId) {
        bo.personnel_uuid = bo.metadata?.uuid || null;
        bo.method = Method.manual;
        array.push(bo);
        return;
      }

      if (!this.personnelsMap.has(bo.keyPersonnelId)) return;

      const personnel = this.personnelsMap.get(bo.keyPersonnelId);
      const model = BeneficialOwnerModel.fromJson(personnel);
      model.personnel_uuid = personnel?.metadata?.uuid || null;
      // model.keyPersonnelId = bo.keyPersonnelId;
      model.method = Method.populate;
      model.metadata = bo.metadata;
      model.id = bo.id;
      array.push(model);
    });

    return array.sort(Helper.sortByOrder);
  }

  bindingPersonnelsToUsPersons(): UsPersonModel[] {
    return (
      this.usPersons?.map(us => {
        if (us.keyPersonnelId && this.personnelsMap.has(us.keyPersonnelId)) {
          const personnel = this.personnelsMap.get(us.keyPersonnelId);
          const model = UsPersonModel.fromJson(personnel);
          model.uuid = personnel?.metadata?.uuid || null;
          model.method = Method.populate;
          model.metadata = us.metadata;
          model.id = us.id;
          if (us.share) model.share = us.share;
          if (us.usCitizenTaxNumber) model.usCitizenTaxNumber = us.usCitizenTaxNumber;
          return model;
        }

        us.uuid = us?.metadata?.uuid || null;
        us.method = Method.manual;
        return us;
      }) || []
    ).sort(Helper.sortByOrder);
  }

  bindingPersonnelsToBoardResolutionApprovingPersons(): BoardResolutionApprovingPersonsModel[] {
    return (
      this.boardResolution?.approvingPersons?.map(person => {
        const model: BoardResolutionApprovingPersonsModel = BoardResolutionApprovingPersonsModel.createEmpty();
        if (person.keyPersonnelId && this.personnelsMap.has(person.keyPersonnelId)) {
          const personnel = this.personnelsMap.get(person.keyPersonnelId);
          model.personnelUuid = personnel?.metadata?.uuid || null;
        }

        return model;
      }) || []
    );
  }

  bindingCorppassProfileToKeyPersonnels(args: { profile: CorppassProfile; singpassMap: ObjectNumber }): PersonnelModel[] {
    const { profile, singpassMap } = args;
    const appointments =
      profile.entity.appointments?.['appointments-list']?.filter(appointment => appointment?.category?.code === CategoryCode.Individual) ||
      [];

    const personnels = appointments.map(appointment => {
      const personnel: DeepPartial<InstanceType<typeof PersonnelModel>> = {
        name: appointment?.['person-reference']?.['person-name']?.value,
        nricNumber: appointment?.['person-reference']?.['idno']?.value,
        email: appointment?.['corppass-email']?.value,
        contactNumber: appointment?.['corppass-mobileno']?.value,
        dateOfAppointmentOnly: appointment?.['appointment-date']?.value,
        // appointment: [mapAppointment(appointment?.position?.code)].filter(Boolean),
        method: ESubmissionMethod.CORPPASS,
      };

      // const nationality = singpassMap.find(v => v.sibs === appointment?.['person-reference']?.nationality?.code || 'SG');
      const nationality = singpassMap[appointment?.['person-reference']?.nationality?.desc?.toLowerCase() || ''];
      nationality && (personnel.nationalityId = nationality);

      const model = PersonnelModel.fromJson(personnel);
      model.uuid = model.metadata?.uuid || null;

      return model;
    });

    return personnels;
  }

  bindingCorppassProfileToUsPersons(args: { profile: CorppassProfile; singpassMap: ObjectNumber }): UsPersonModel[] {
    const { profile, singpassMap } = args;
    const shareholders =
      profile.entity.shareholders?.['shareholders-list']?.filter(appointment => appointment?.category?.code === CategoryCode.Individual) ||
      [];

    const personnels = shareholders.map(shareholder => {
      const us: DeepPartial<InstanceType<typeof UsPersonModel>> = {
        name: shareholder?.['person-reference']?.['person-name']?.value,
        nricNumber: shareholder?.['person-reference']?.['idno']?.value,
        email: shareholder?.['corppass-email']?.value,
        contactNumber: shareholder?.['corppass-mobileno']?.value,
        method: Method.corppass,
      };

      const model = UsPersonModel.fromJson(us);
      model.uuid = model?.metadata?.uuid || null;

      return model;
    });

    return personnels;
  }
}

// TODO: PUT TO RIGHT PLACE
const mapAppointment = (code?: PositionCode): PersonnelAppointment | undefined => {
  const appointmentMap: { [k in PositionCode]?: PersonnelAppointment } = {
    [PositionCode.Director]: PersonnelAppointment.DIRECTOR,
    [PositionCode.Secretary]: PersonnelAppointment.SECRETARY,
  };

  return code ? appointmentMap[code] : undefined;
};
